import { useEffect, useState } from "react";
import { AlertTriangle, Tag, X } from "lucide-react";

import { Modal } from "@/components/Modal";
import type { LiveListing } from "@/context/AppContext";
import { sendEbayOfferToWatchers } from "@/features/ebayListings";

interface Props {
  listing: LiveListing | null;
  watcherCount: number;
  onClose: () => void;
  onSuccess: (text: string, watcherCount: number) => void;
}

export function SendOfferModal({
  listing,
  watcherCount,
  onClose,
  onSuccess,
}: Props) {
  const open = listing !== null;
  const [discountType, setDiscountType] = useState<"percent" | "amount">(
    "percent",
  );
  const [discountValue, setDiscountValue] = useState("10");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!listing) return;
    setDiscountType("percent");
    setDiscountValue("10");
    setMessage("");
    setError(null);
    setSubmitting(false);
  }, [listing]);

  if (!listing) return null;

  const parsed = parseFloat(discountValue);
  let preview: string | null = null;
  if (Number.isFinite(parsed) && parsed > 0) {
    if (discountType === "percent") {
      const pct = Math.round(parsed);
      if (pct >= 5 && pct <= 50) {
        const newPrice = listing.askingPrice * (1 - pct / 100);
        preview = `Buyers see $${newPrice.toFixed(2)} (${pct}% off $${listing.askingPrice.toFixed(2)})`;
      }
    } else if (parsed < listing.askingPrice) {
      const pct = Math.ceil(
        ((listing.askingPrice - parsed) / listing.askingPrice) * 100,
      );
      if (pct >= 5 && pct <= 50) {
        const actualPrice = listing.askingPrice * (1 - pct / 100);
        preview =
          Math.abs(actualPrice - parsed) < 0.005
            ? `Buyers see $${actualPrice.toFixed(2)} (${pct}% off)`
            : `eBay rounds to whole percents — buyers will see $${actualPrice.toFixed(2)} (${pct}% off, not $${parsed.toFixed(2)})`;
      }
    }
  }

  const submit = async () => {
    if (!listing.ebayListingId) return;
    const value = parseFloat(discountValue);
    if (!Number.isFinite(value) || value <= 0) {
      return setError(
        discountType === "percent"
          ? "Enter a percent between 5 and 50"
          : "Enter a valid offer price",
      );
    }
    if (discountType === "percent" && (value < 5 || value > 50)) {
      return setError("Percent discount must be between 5 and 50");
    }
    if (discountType === "amount" && value >= listing.askingPrice) {
      return setError(
        `Offer price must be less than the current price ($${listing.askingPrice.toFixed(2)})`,
      );
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await sendEbayOfferToWatchers({
        itemId: listing.ebayListingId,
        discountType,
        discountValue: value,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      const n = result.watcherCount ?? 0;
      onSuccess(
        `Offer submitted at $${(result.newPrice ?? 0).toFixed(2)} — eBay will deliver it to eligible watchers (~${n} estimated)`,
        n,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      ariaLabel="Send offer to watchers"
      panelClassName="w-[min(480px,92vw)] max-h-[85vh] flex flex-col"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold text-foreground">
          Send offer to watchers
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 overflow-y-auto p-4">
        <p className="text-xs text-muted-foreground">
          eBay will send this 2-day Buy It Now offer to eligible watchers (~
          {watcherCount} based on the current count — eBay may filter some out).
        </p>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            Discount type
          </label>
          <div className="flex overflow-hidden rounded-md border border-border">
            {(["percent", "amount"] as const).map((opt) => {
              const active = discountType === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    setDiscountType(opt);
                    setDiscountValue(
                      opt === "percent"
                        ? "10"
                        : (listing.askingPrice * 0.9).toFixed(2),
                    );
                    setError(null);
                  }}
                  disabled={submitting}
                  className={
                    active
                      ? "flex-1 bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground"
                      : "flex-1 bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                  }
                >
                  {opt === "percent" ? "% off" : "$ price"}
                </button>
              );
            })}
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            {discountType === "percent"
              ? "Percent off (5–50, whole %)"
              : "New offer price"}
          </label>
          <input
            type="text"
            inputMode={discountType === "percent" ? "numeric" : "decimal"}
            value={discountValue}
            onChange={(e) => {
              const cleaned =
                discountType === "percent"
                  ? e.target.value.replace(/[^0-9]/g, "").slice(0, 2)
                  : e.target.value;
              setDiscountValue(cleaned);
              setError(null);
            }}
            placeholder={discountType === "percent" ? "10" : "0.00"}
            disabled={submitting}
            autoFocus
            className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
          />
          {preview && (
            <p className="text-[11px] text-muted-foreground">{preview}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <label className="text-xs font-medium text-muted-foreground">
              Message (optional)
            </label>
            <span className="text-[11px] text-muted-foreground">
              {message.length}/500
            </span>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Limited time offer just for watchers!"
            disabled={submitting}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:border-secondary focus:outline-none disabled:opacity-50"
          />
        </div>
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive bg-destructive/10 p-3 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-md bg-muted px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <Tag className="h-3.5 w-3.5" />
          {submitting ? "Sending…" : "Send offer"}
        </button>
      </div>
    </Modal>
  );
}
